/**
 * Shared types for the Storefront-API collection filtering engine.
 *
 * See docs/storefront-filtering.md. This layer maps Merchology's *current* product
 * data (tags, vendor, productType, price) to Storefront API `ProductFilter` inputs.
 */

export type FilterGroupType = 'vendor' | 'productType' | 'price' | 'tag' | 'variantOption';

export type ProductFilterInput =
  | { available: boolean }
  | { price: { min?: number; max?: number } }
  | { productType: string }
  | { productVendor: string }
  | { tag: string }
  | { variantOption: { name: string; value: string } };

export interface PriceRange {
  min?: number;
  max?: number;
}

export interface FilterGroupConfig {
  key: string;
  label: string;
  type: FilterGroupType;
  /**
   * For `tag` groups: how a selected value becomes a product tag.
   *  - prefix style groups: `material=` -> tag `material=Cotton`
   *  - color: sentinel `FILTER` -> tag `FilterDarkGrey` (`Filter` + value w/o spaces)
   */
  tagPrefix?: string;
  optionName?: string;
  /** Canonical value -> every underlying spelling of it, OR'd together when selected. */
  valueSynonyms?: Record<string, string[]>;
  primaryRank?: number;
}

export type FilterSelection = Record<string, string[]>;

export interface ClientRefinement {
  key: string;
  tags: string[];
}

export interface BuiltFilters {
  apiFilters: ProductFilterInput[];
  clientRefinements: ClientRefinement[];
  /** Refined on the client against each product's MIN price; the native `price` filter
   *  matches ANY variant, so it over-includes cheaper-variant products. */
  priceRanges: PriceRange[];
}

export interface Money {
  amount: string;
  currencyCode: string;
}

export interface ProductNode {
  id: string;
  handle: string;
  title: string;
  vendor: string;
  productType: string;
  tags: string[];
  availableForSale: boolean;
  createdAt?: string;
  onlineStoreUrl?: string | null;
  featuredImage?: { url: string; altText?: string | null } | null;
  priceRange?: {
    minVariantPrice: Money;
    maxVariantPrice: Money;
  };
  compareAtPriceRange?: {
    minVariantPrice: Money;
  };
}

export interface PageContext {
  collectionHandle: string;
  customerType?: string;
  /** Employee flags: 'merch_store_merchologist' | 'sales_team' | ''. */
  ctags?: string;
  colorTags?: string[];
  colorHexMap?: Record<string, string>;
  requestPath?: string;
  /** `collection.current_tags` — the legacy tag-filter segment of the URL, if any. */
  legacyTags?: string[];
  discounts?: Record<string, { range: Record<string, unknown> }>;
}

export interface FacetValue {
  label: string;
  count: number;
  /** Raw `input` JSON string Shopify returns, e.g. `{"tag":"FilterBlack"}`. */
  input: string;
}

export interface FacetGroup {
  id: string;
  label: string;
  type: string;
  values: FacetValue[];
}

export interface CollectionSort {
  sortKey: string;
  reverse: boolean;
}

export interface FilterPageResult {
  products: ProductNode[];
  facets: FacetGroup[];
  endCursor: string | null;
  hasNextPage: boolean;
  clientRefined: boolean;
  /** Exact number of products the API filters match, or null when it can't be trusted
   *  (client refinement in play, or every usable facet is self-excluded/capped). */
  apiTotal: number | null;
  /** `apiTotal` hit Shopify's 5,000 facet-count ceiling — display it as "5,000+". */
  apiTotalCapped: boolean;
}

export type GraphQLRequest = (
  query: string,
  variables: Record<string, unknown>
) => Promise<any>;
